import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Typography, 
  TextField, 
  Button, 
  Paper, 
  Box, 
  MenuItem,
  FormControl,
  FormLabel,
  Select,
  InputLabel,
  Snackbar,
  Alert,
  useMediaQuery,
  IconButton
} from '@mui/material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { useAuth } from '../contexts/AuthContext';
import { apiService } from '../utils/api';
import axios from 'axios';
import config from '../config';

const ReportForm = () => {
  const { currentUser, isLoggedIn } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const reportedUser = searchParams.get('user');
  const isMobile = useMediaQuery('(max-width:600px)');
  
  // フォーム状態
  const [formValues, setFormValues] = useState({
    reporter_username: currentUser ? currentUser.username : '',
    violation_type: '',
    incident_time: new Date().toISOString().substring(0, 16), // YYYY-MM-DDThh:mm 形式
    reported_username: reportedUser || '',
    description: ''  // 詳細フィールド用の状態を追加
  });
  
  // URLパラメータからユーザー名が渡された場合に適用
  useEffect(() => {
    if (reportedUser) {
      setFormValues(prev => ({
        ...prev,
        reported_username: reportedUser
      }));
    }
  }, [reportedUser]);
  
  // バリデーション状態
  const [formErrors, setFormErrors] = useState({
    reporter_username: false,
    violation_type: false,
    incident_time: false,
    reported_username: false
  });
  
  // 通知状態
  const [notification, setNotification] = useState({
    open: false,
    message: '',
    severity: 'info'
  });
  
  // 違反種類のオプション
  const violationOptions = [
    { value: 'harassment', label: '攻撃的な行為・嫌がらせ' },
    { value: 'violence', label: '暴力的な発言' },
    { value: 'privacy', label: 'プライバシーの侵害' },
    { value: 'spam', label: 'スパム' },
    { value: 'impersonation', label: 'なりすまし' },
    { value: 'solicitation', label: '勧誘' },
    { value: 'other', label: 'その他' }
  ];
  
  // 入力変更ハンドラ
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormValues({
      ...formValues,
      [name]: value
    });
    
    // エラー状態をリセット
    setFormErrors({
      ...formErrors,
      [name]: false
    });
  };
  
  // フォーム送信ハンドラ
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 未ログインユーザーはログインページにリダイレクト
    if (!isLoggedIn) {
      navigate('/login', { state: { returnUrl: '/report' } });
      return;
    }
    
    // バリデーション
    const errors = {
      reporter_username: !formValues.reporter_username,
      violation_type: !formValues.violation_type,
      incident_time: !formValues.incident_time,
      reported_username: !formValues.reported_username
    };
    
    setFormErrors(errors);
    
    if (Object.values(errors).some(error => error)) {
      setNotification({
        open: true,
        message: '全ての項目を入力してください',
        severity: 'error'
      });
      return;
    }
    
    try {
      // 通報データの送信前に時刻を適切なISO形式に変換
      const submissionData = {
        ...formValues,
        incident_time: new Date(formValues.incident_time).toISOString()
      };
      
      // 認証トークンを直接取得して設定
      const authToken = localStorage.getItem('authToken');
      
      if (!authToken) {
        throw new Error('認証情報が見つかりません。ログインし直してください。');
      }
      
      // axiosを直接使用してトークンを明示的に設定
      const response = await axios.post(`${config.API_BASE_URL}/api/reports/create`, submissionData, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        withCredentials: true
      });
      
      if (response.data && response.data.status === 'success') {
        setNotification({
          open: true,
          message: '通報が送信されました。管理者が確認します。',
          severity: 'success'
        });
        
        // フォームをリセット（通報者名は保持）
        setFormValues({
          reporter_username: currentUser ? currentUser.username : '',
          violation_type: '',
          incident_time: new Date().toISOString().substring(0, 16),
          reported_username: '',
          description: ''  // 詳細フィールドもリセット
        });
      } else {
        throw new Error(response.data?.message || '通報の送信に失敗しました');
      }
    } catch (error) {
      
      setNotification({
        open: true,
        message: `エラー: ${error.message || '通報の送信に失敗しました'}`,
        severity: 'error'
      });
    }
  };
  
  // 通知を閉じる
  const handleCloseNotification = () => {
    setNotification({
      ...notification,
      open: false
    });
  };
  
  return (
    <Container maxWidth="md">
      <Paper elevation={3} sx={{ p: 4, mt: 4, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
          <IconButton 
            onClick={() => navigate(-1)}
            sx={{ mr: 2 }}
            aria-label="戻る"
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" component="h1">
            違反行為の通報
          </Typography>
        </Box>
        
        {reportedUser && (
          <Box sx={{ 
            p: 2, 
            bgcolor: 'info.light', 
            color: 'info.contrastText', 
            borderRadius: 1, 
            mb: 3 
          }}>
            <Typography variant="body1">
              ユーザー <strong>@{reportedUser}</strong> に関する通報を行います
            </Typography>
          </Box>
        )}
        
        <Typography variant="body1" paragraph sx={{ mb: 3 }}>
          このフォームは、コミュニティガイドラインに違反する行為をご報告いただくためのものです。
          ご報告いただいた内容は慎重に調査し、適切な対応を取らせていただきます。
        </Typography>
        
        <Box component="form" onSubmit={handleSubmit} noValidate>
          {/* 通報者名（ログイン中のユーザー、自動入力、編集不可） */}
          <TextField 
            margin="normal"
            fullWidth
            label="通報者名"
            name="reporter_username"
            value={formValues.reporter_username}
            onChange={handleChange}
            error={formErrors.reporter_username}
            helperText={formErrors.reporter_username ? "通報者名を入力してください" : ""}
            disabled={!!currentUser}
            InputProps={{
              readOnly: !!currentUser,
            }}
          />
          
          {/* 違反種類 */}
          <FormControl 
            fullWidth 
            margin="normal"
            error={formErrors.violation_type}
          >
            <InputLabel id="violation-type-label">違反の種類</InputLabel>
            <Select
              labelId="violation-type-label"
              id="violation-type"
              name="violation_type"
              value={formValues.violation_type}
              onChange={handleChange}
              label="違反の種類"
            >
              {violationOptions.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {formErrors.violation_type && (
              <Typography variant="caption" color="error">
                違反の種類を選択してください
              </Typography>
            )}
          </FormControl>
          
          {/* 違反行為の時間 */}
          <TextField 
            margin="normal"
            fullWidth
            label="違反行為の日時"
            name="incident_time"
            type="datetime-local"
            value={formValues.incident_time}
            onChange={handleChange}
            error={formErrors.incident_time}
            helperText={formErrors.incident_time ? "違反行為の時間を入力してください" : ""}
            InputLabelProps={{
              shrink: true,
            }}
          />
          
          {/* 違反ユーザー名 */}
          <TextField 
            margin="normal"
            fullWidth
            label="違反を行ったユーザー名"
            name="reported_username"
            value={formValues.reported_username}
            onChange={handleChange}
            error={formErrors.reported_username}
            helperText={formErrors.reported_username ? "違反を行ったユーザー名を入力してください" : ""}
            disabled={!!reportedUser}
            InputProps={{
              readOnly: !!reportedUser,
            }}
          />
          
          {/* 詳細情報（オプション） */}
          <TextField 
            margin="normal"
            fullWidth
            label="詳細情報（オプション）"
            name="description"
            multiline
            rows={4}
            value={formValues.description || ''}
            onChange={handleChange}
            helperText="違反行為の具体的な内容をご記入ください"
          />
          
          <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
            <Button 
              variant="outlined" 
              onClick={() => navigate(-1)}
            >
              キャンセル
            </Button>
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
            >
              通報を送信
            </Button>
          </Box>
        </Box>
      </Paper>
      
      {/* 通知 */}
      <Snackbar 
        open={notification.open} 
        autoHideDuration={6000} 
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseNotification} 
          severity={notification.severity} 
          sx={{ width: '100%' }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default ReportForm; 